import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { 
    getMyStore, 
    updateStoreInfo, 
    updateStoreSalesStatus,
    getStates
} from '../../services/supabaseService';

export const ManageStore: React.FC = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [store, setStore] = useState<any>(null);
    const [salesEnabled, setSalesEnabled] = useState<boolean>(false);
    const [togglingSales, setTogglingSales] = useState<boolean>(false);
    const [statesList, setStatesList] = useState<any[]>([]);

    const [formData, setFormData] = useState({
        name: '',
        logo: '',
        site: '',
        endereco: '',
        bairro: '',
        cidade: '',
        estado: '',
        state_id: '',
        whatsapp: '',
        instagram: '',
        about: '',
        cep: ''
    });

    useEffect(() => {
        if (user && user.role_id === 6) {
            fetchData();
        } else if (user) {
            navigate('/');
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statesData, myStore] = await Promise.all([
                getStates(),
                getMyStore()
            ]);
            setStatesList(statesData || []);

            if (myStore) {
                setStore(myStore);
                setSalesEnabled(!!myStore.sales_enabled);
                setFormData({
                    name: myStore.name || '',
                    logo: myStore.logo || '',
                    site: myStore.site || '',
                    endereco: myStore.address || myStore.endereco || '',
                    bairro: myStore.neighborhood || myStore.bairro || '',
                    cidade: myStore.city || myStore.cidade || '',
                    estado: myStore.estado || '',
                    state_id: myStore.state_id ? String(myStore.state_id) : '',
                    whatsapp: (myStore.contact?.whatsapp || myStore.contato?.whatsapp) || '',
                    instagram: (myStore.social_links?.instagram || myStore.redes_sociais?.instagram) || '',
                    about: myStore.about || '',
                    cep: myStore.cep || myStore.contact?.cep || myStore.contato?.cep || ''
                });
            }
        } catch (error) {
            showNotification("Erro ao carregar dados da loja", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleSales = async (targetStatus: boolean) => {
        if (!store) return;
        setTogglingSales(true);
        try {
            const success = await updateStoreSalesStatus(store.id, targetStatus);
            if (success) {
                setSalesEnabled(targetStatus);
                setStore((prev: any) => prev ? { ...prev, sales_enabled: targetStatus } : null);
                showNotification(
                    targetStatus 
                        ? "Vendas ativadas! Sua loja agora pode receber pedidos e novas compras." 
                        : "Vendas desativadas! Sua loja foi pausada para novos pedidos.", 
                    "success"
                );
            } else {
                showNotification("Não foi possível alterar o status das vendas.", "error");
            }
        } catch (err) {
            showNotification("Erro ao atualizar status das vendas", "error");
        } finally {
            setTogglingSales(false);
        }
    };

    const handleSaveInfo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!store) return;
        setSaving(true);
        try {
            const dataToUpdate = {
                name: formData.name,
                logo: formData.logo,
                site: formData.site,
                address: formData.endereco,
                neighborhood: formData.bairro,
                city: formData.cidade,
                state_id: formData.state_id ? Number(formData.state_id) : undefined,
                about: formData.about,
                cep: formData.cep,
                sales_enabled: salesEnabled,
                contact: { whatsapp: formData.whatsapp, cep: formData.cep },
                social_links: { instagram: formData.instagram }
            };
            const success = await updateStoreInfo(store.id, dataToUpdate);
            if (success) {
                showNotification("Dados da loja atualizados com sucesso!", "success");
            } else {
                showNotification("Não foi possível salvar os dados da loja", "error");
            }
        } catch (error) {
            showNotification("Erro ao salvar dados da loja", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest text-xs animate-pulse">
                Carregando dados da loja...
            </div>
        );
    }

    if (!store) {
        return (
            <div className="p-12 text-center text-slate-400 font-bold text-sm">
                Loja não encontrada ou você não possui permissão de lojista.
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
                <div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400 mb-1">
                        <span>Configurações</span>
                        <span>/</span>
                        <span className="text-white">Dados da Loja</span>
                    </div>
                    <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                        <i className="fas fa-store text-purple-500 text-xl"></i>
                        Dados da Loja
                    </h1>
                    <p className="text-slate-400 text-xs mt-0.5">
                        Gerencie as informações principais do seu perfil, contato, endereço e status de vendas.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <Link
                        to={`/loja/${store.slug}`}
                        target="_blank"
                        className="px-3.5 py-2 bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl text-xs font-bold border border-slate-700/60 transition-all flex items-center gap-2"
                        title="Abrir página pública da loja"
                    >
                        <i className="fas fa-arrow-up-right-from-square text-xs text-purple-400"></i>
                        Ver Perfil Público
                    </Link>
                </div>
            </header>

            {/* Sales Status Banner */}
            <div className={`border rounded-2xl p-5 transition-all ${
                salesEnabled 
                    ? 'bg-gradient-to-r from-emerald-950/40 via-slate-900 to-slate-900 border-emerald-500/30' 
                    : 'bg-gradient-to-r from-amber-950/30 via-slate-900 to-slate-900 border-amber-500/30'
            }`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1 max-w-xl">
                        <div className="flex items-center gap-2.5">
                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${salesEnabled ? 'bg-emerald-500 animate-pulse shadow-sm shadow-emerald-500/50' : 'bg-amber-500'}`}></span>
                            <h2 className="text-sm font-black text-white tracking-wide uppercase">
                                Status das Vendas: {salesEnabled ? (
                                    <span className="text-emerald-400">Ativadas</span>
                                ) : (
                                    <span className="text-amber-400">Pausadas</span>
                                )}
                            </h2>
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed">
                            {salesEnabled ? (
                                <>Sua loja está ativa para receber pedidos online. Clientes e jogadores podem adicionar cartas ao carrinho e finalizar compras.</>
                            ) : (
                                <>As vendas online estão pausadas. Usuários podem visualizar sua vitrine, mas o botão de compra fica temporariamente desabilitado.</>
                            )}
                        </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            type="button"
                            disabled={togglingSales}
                            onClick={() => handleToggleSales(!salesEnabled)}
                            className={`relative inline-flex h-7 w-14 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-950 disabled:opacity-50 ${
                                salesEnabled ? 'bg-emerald-500' : 'bg-slate-700'
                            }`}
                            role="switch"
                            aria-checked={salesEnabled}
                        >
                            <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                    salesEnabled ? 'translate-x-8' : 'translate-x-1'
                                }`}
                            />
                        </button>
                        <span className={`text-xs font-black uppercase tracking-wider min-w-[60px] ${salesEnabled ? 'text-emerald-400' : 'text-slate-400'}`}>
                            {togglingSales ? 'Salvando...' : (salesEnabled ? 'Ativo' : 'Pausado')}
                        </span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form column */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-7 space-y-5">
                        <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                            <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                                <span className="w-1.5 h-4 bg-purple-500 rounded-full"></span>
                                Informações da Empresa
                            </h2>
                        </div>

                        <form onSubmit={handleSaveInfo} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase text-slate-400">Nome da Loja</label>
                                    <input 
                                        type="text"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-purple-500 outline-none transition-colors"
                                        value={formData.name || ''}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                        <label className="text-[10px] font-black uppercase text-slate-400">Logo URL</label>
                                        <Link 
                                            to="/minha-loja/upload" 
                                            className="text-[10px] text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1 transition-colors"
                                            title="Fazer upload no Cloudinary"
                                        >
                                            <i className="fas fa-cloud-arrow-up text-[9px]"></i>
                                            Upload Cloudinary
                                        </Link>
                                    </div>
                                    <input 
                                        type="text"
                                        placeholder="https://res.cloudinary.com/..."
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-purple-500 outline-none transition-colors"
                                        value={formData.logo || ''}
                                        onChange={(e) => setFormData({...formData, logo: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase text-slate-400">Site Oficial (Opcional)</label>
                                    <input 
                                        type="text"
                                        placeholder="https://sualoja.com.br"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-purple-500 outline-none transition-colors"
                                        value={formData.site || ''}
                                        onChange={(e) => setFormData({...formData, site: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase text-slate-400">WhatsApp de Contato</label>
                                    <input 
                                        type="text"
                                        placeholder="(11) 99999-9999"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-purple-500 outline-none transition-colors"
                                        value={formData.whatsapp || ''}
                                        onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400">Instagram (Usuário ou Link)</label>
                                    <input 
                                        type="text"
                                        placeholder="@sualoja ou link do perfil"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-purple-500 outline-none transition-colors"
                                        value={formData.instagram || ''}
                                        onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-1 pt-2">
                                <label className="text-[10px] font-black uppercase text-slate-400">Sobre a Loja / Apresentação</label>
                                <textarea 
                                    rows={3}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-purple-500 outline-none transition-colors resize-none"
                                    value={formData.about || ''}
                                    onChange={(e) => setFormData({...formData, about: e.target.value})}
                                    placeholder="Conte aos jogadores a história da sua loja, comunidade e diferenciais..."
                                />
                            </div>

                            <div className="pt-2 border-t border-slate-800/80">
                                <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-3">Endereço e Localização</h3>
                                <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
                                    <div className="col-span-2 md:col-span-3 space-y-1">
                                        <label className="text-[10px] font-black uppercase text-slate-400">CEP</label>
                                        <input 
                                            type="text"
                                            maxLength={9}
                                            placeholder="00000-000"
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:border-purple-500 outline-none"
                                            value={formData.cep || ''}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/\D/g, '').substring(0, 8);
                                                let formatted = raw;
                                                if (raw.length > 5) {
                                                    formatted = `${raw.substring(0, 5)}-${raw.substring(5)}`;
                                                }
                                                setFormData({...formData, cep: formatted});
                                            }}
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-5 space-y-1">
                                        <label className="text-[10px] font-black uppercase text-slate-400">Endereço (Rua e Nº)</label>
                                        <input 
                                            type="text"
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:border-purple-500 outline-none"
                                            value={formData.endereco || ''}
                                            onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-span-1 md:col-span-2 space-y-1">
                                        <label className="text-[10px] font-black uppercase text-slate-400">Bairro</label>
                                        <input 
                                            type="text"
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:border-purple-500 outline-none"
                                            value={formData.bairro || ''}
                                            onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-span-1 md:col-span-2 space-y-1">
                                        <label className="text-[10px] font-black uppercase text-slate-400">UF</label>
                                        <select 
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-2 py-2 text-xs text-white focus:border-purple-500 outline-none uppercase text-center"
                                            value={formData.state_id || ''}
                                            onChange={(e) => {
                                                const selectedId = e.target.value;
                                                const selectedStateObj = statesList.find(s => String(s.id) === String(selectedId));
                                                setFormData({
                                                    ...formData,
                                                    state_id: selectedId,
                                                    estado: selectedStateObj?.code || ''
                                                });
                                            }}
                                        >
                                            <option value="">UF</option>
                                            {statesList.map((st) => (
                                                <option key={st.id} value={st.id}>
                                                    {st.code}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-2 md:col-span-4 space-y-1">
                                        <label className="text-[10px] font-black uppercase text-slate-400">Cidade</label>
                                        <input 
                                            type="text"
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:border-purple-500 outline-none"
                                            value={formData.cidade || ''}
                                            onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-slate-800 flex justify-end">
                                <button 
                                    type="submit"
                                    disabled={saving}
                                    className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-8 py-2.5 rounded-xl shadow-md shadow-purple-600/20 active:scale-95 text-xs uppercase tracking-wider transition-all flex items-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin text-xs"></i>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-check text-xs"></i>
                                            Salvar Dados da Loja
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Sidebar Cards */}
                <div className="space-y-5">
                    {/* Visualização da Vitrine */}
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 text-center">
                        {formData.logo ? (
                            <img 
                                src={formData.logo} 
                                alt={formData.name} 
                                className="w-16 h-16 rounded-2xl mx-auto mb-3 object-cover border border-slate-800 shadow-md"
                                onError={(e) => {
                                    (e.target as HTMLElement).style.display = 'none';
                                }}
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 mx-auto mb-3">
                                <i className="fas fa-store text-xl"></i>
                            </div>
                        )}
                        <h3 className="text-xs font-black text-white uppercase tracking-wider mb-1 truncate">{formData.name || 'Sua Loja'}</h3>
                        <p className="text-[11px] text-slate-400 mb-3">
                            {formData.cidade && formData.estado ? `${formData.cidade} - ${formData.estado}` : 'Localização não definida'}
                        </p>
                        <div className="mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-950 border border-slate-800">
                            <span className={`w-2 h-2 rounded-full ${salesEnabled ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`}></span>
                            <span className={salesEnabled ? 'text-emerald-400' : 'text-amber-400'}>
                                {salesEnabled ? 'Vendas Ativas' : 'Vendas Pausadas'}
                            </span>
                        </div>
                        <Link 
                            to={`/loja/${store.slug}`}
                            className="w-full bg-slate-800 hover:bg-slate-750 text-slate-200 font-bold text-xs px-4 py-2.5 rounded-xl border border-slate-700 transition-all flex items-center justify-center gap-2"
                        >
                            <i className="fas fa-eye text-xs text-purple-400"></i>
                            Ver Perfil Público
                        </Link>
                    </div>

                    {/* Central de Suporte */}
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 text-center">
                        <i className="fas fa-circle-question text-2xl text-slate-600 mb-2 block"></i>
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Precisa de Ajuda?</h3>
                        <p className="text-[11px] text-slate-400 mb-4">Dúvidas sobre configurações de loja ou verificação de conta?</p>
                        <Link 
                            to="/suporte" 
                            className="inline-block text-xs font-black text-purple-400 hover:text-purple-300 transition-colors uppercase tracking-wider"
                        >
                            Abrir Chamado no Suporte →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};
